<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import UpsertOrganizationEmployeeForm from '@/components/forms/organizationEmployee/UpsertOrganizationEmployeeForm.vue';
import { useEmployeeStore } from '@/stores/employee';
import type { Employee, OrganizationEmployee } from '@/types/employee';
import type { Organization } from '@/types/organization';
import type { UpsertOrganizationEmployeesRequest } from '@/types/requests/organizationEmployees/upsertOrganizationEmployeesRequest';
import { getEmployeeId } from '@/utils/employee';
import toast from '@/utils/toast';
import { ref, useTemplateRef } from 'vue';

const props = defineProps<{
    organization?: Organization;
    organizationEmployees?: OrganizationEmployee[];
}>();

const emit = defineEmits(['closeDialog']);

const employeeStore = useEmployeeStore();

const selectedEmployees = ref<OrganizationEmployee[]>([]);

const upsertOrgEmpForm = useTemplateRef('form');

function closeDialog() {
    clearInput();
    emit('closeDialog');
}

function clearInput() {
    selectedEmployees.value = [];
}

async function link() {
    if (!upsertOrgEmpForm.value) {
        toast.error('EmployeeFailedLink');
        return;
    }

    await upsertOrgEmpForm.value.submit(selectedEmployees.value.map((e) => getEmployeeId(e.employee)));
    toast.success('EmployeesLinked');
    updateEmployeeList(
        selectedEmployees.value.map((orgEmp: OrganizationEmployee) => orgEmp.employee as Employee),
        upsertOrgEmpForm.value.request
    );
    closeDialog();
}

function updateEmployeeList(linkedEmployees: Employee[], request: UpsertOrganizationEmployeesRequest) {
    linkedEmployees.forEach((employee) => {
        employeeStore.employeesInOrganizations.push({ employee, ...request });
    });
}
</script>

<template v-slot:default>
    <v-card-text>
        <v-select
            v-model="selectedEmployees"
            label="Medewerkers"
            required
            :items="employeeStore.filteredOrganizationEmployees"
            :item-title="(item) => (item.employee as Employee).fullName"
            multiple
            return-object
        />
        <UpsertOrganizationEmployeeForm :organization-id="props.organization._id" ref="form" v-if="props.organization" />
    </v-card-text>
    <v-card-actions>
        <v-spacer />
        <CustomButton class="mr-2" @click="link" text="Bevestigen" />
        <CustomButton color="red" @click="closeDialog" text="Annuleren" />
    </v-card-actions>
</template>
